import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Loader2, BarChart3, Info } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";

const uf = (n) => (n == null ? "—" : `${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 1 })} UF`);
const pct = (n) => (n == null ? "—" : `${n}%`);
const num = (n) => (n == null ? "—" : Number(n).toLocaleString("es-CL"));
const clp = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-CL")}`);

const plural = (n, one, many) => `${num(n)} ${n === 1 ? one : many}`;

const Kpi = ({ label, value, hint, testId, tone = "text-primary" }) => (
  <div className="bg-white rounded-2xl border border-border p-5" data-testid={testId}>
    <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
    <p className={`text-3xl font-black mt-1 ${tone}`}>{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const Section = ({ title, explain, children, testId }) => (
  <section className="bg-white rounded-2xl border border-border p-6" data-testid={testId}>
    <h2 className="text-lg font-semibold">{title}</h2>
    {explain && <p className="text-sm text-muted-foreground mt-1 mb-4 flex gap-1.5"><Info className="w-4 h-4 mt-0.5 shrink-0" />{explain}</p>}
    {children}
  </section>
);

const CategoryTable = ({ rows, total }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="stats-category-table">
      <thead className="bg-secondary/60 text-left text-[11px] uppercase text-slate-500">
        <tr>{["Categoría", "Cuota", "Solicitadas", "Aprobadas", "Rechazadas", "En proceso", "% promedio otorgado", "% prom. solicitado", "Socio acepta", "No acepta", "% rechazo socio", "Sin respuesta", "Desviación", "Cuota (aceptaron)", "Beca otorgada", "Ingreso generado"].map((h) => <th key={h} className="px-2 py-2.5 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {[...rows, { categoria: "TOTAL", ...total, _total: true }].map((r) => (
          <tr key={r.categoria} className={`border-t border-border ${r._total ? "bg-primary/5 font-bold" : ""}`}>
            <td className="px-2 py-2.5 font-semibold text-primary whitespace-nowrap">{r.categoria}</td>
            <td className="px-2 py-2.5 whitespace-nowrap">{r._total ? "" : uf(r.cuota_uf)}</td>
            <td className="px-2 py-2.5">{num(r.solicitadas)}</td>
            <td className="px-2 py-2.5 text-emerald-700">{num(r.aprobadas)}</td>
            <td className="px-2 py-2.5 text-red-700">{num(r.rechazadas)}</td>
            <td className="px-2 py-2.5">{num(r.en_proceso + r.info)}</td>
            <td className="px-2 py-2.5 font-semibold">{pct(r.pct_promedio)}</td>
            <td className="px-2 py-2.5">{pct(r.pct_solicitado_promedio)}</td>
            <td className="px-2 py-2.5">{num(r.socio_acepta)}</td>
            <td className="px-2 py-2.5">{num(r.socio_no_acepta)}</td>
            <td className="px-2 py-2.5 text-red-700 font-semibold">{pct(r.tasa_rechazo_socio)}</td>
            <td className="px-2 py-2.5">{num(r.socio_sin_respuesta)}</td>
            <td className="px-2 py-2.5 whitespace-nowrap">{dev(r.desviacion_promedio)}</td>
            <td className="px-2 py-2.5 whitespace-nowrap">{uf(r.cuota_total_uf)}</td>
            <td className="px-2 py-2.5 text-orange-700 font-semibold whitespace-nowrap">{uf(r.beca_uf)}</td>
            <td className="px-2 py-2.5 text-emerald-700 font-semibold whitespace-nowrap">{uf(r.ingreso_uf)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Chart = ({ data, x, bars, height = 260 }) => (
  <div style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
        <XAxis dataKey={x} tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-slate-700">{v}</span>}
          payload={bars.map((b) => ({ value: b.name, type: "square", color: b.color, id: b.key }))} />
        {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[4, 4, 0, 0]} />)}
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const FamilyBox = ({ label, value, of, testId }) => (
  <div className="rounded-xl bg-secondary/50 p-4" data-testid={testId}>
    <p className="text-2xl font-black text-primary">{num(value)}</p>
    <p className="text-sm">{label}</p>
    {of ? <p className="text-xs text-muted-foreground">{Math.round((100 * value) / of)}% de las familias con hijos</p> : null}
  </div>
);

const SLICES = [
  { key: "ingreso_uf", name: "Pagan los socios", color: "#047857" },
  { key: "beca_uf", name: "Beca que otorga el EIM", color: "#C2410C" },
];

const dev = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n} pts`);

const CuotaPie = ({ t }) => {
  const data = SLICES.map((s) => ({ ...s, value: t[s.key] || 0 })).filter((s) => s.value > 0);
  const share = (v) => (t.cuota_total_uf ? Math.round((100 * v) / t.cuota_total_uf) : 0);
  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-center">
      <div style={{ height: 280 }}>
        {data.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={70} outerRadius={120} paddingAngle={2}>
                {data.map((s) => <Cell key={s.key} fill={s.color} />)}
              </Pie>
              <Tooltip formatter={(v) => uf(v)} />
            </PieChart>
          </ResponsiveContainer>
        ) : <div className="h-full rounded-full border-8 border-secondary flex items-center justify-center text-sm text-muted-foreground">Sin datos</div>}
      </div>
      <div className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase text-slate-500">Cuota completa de los socios becados que aceptaron</p>
          <p className="text-4xl font-black text-primary" data-testid="pie-cuota-potencial">{uf(t.cuota_total_uf)}</p>
        </div>
        {SLICES.map((s) => (
          <div key={s.key} className="flex items-center gap-3" data-testid={`pie-${s.key}`}>
            <span className="w-4 h-4 rounded" style={{ background: s.color }} />
            <span className="flex-1 text-sm">{s.name}</span>
            <strong className="text-lg" style={{ color: s.color }}>{uf(t[s.key])}</strong>
            <span className="text-sm text-muted-foreground w-12 text-right">{share(t[s.key])}%</span>
          </div>
        ))}
        <p className="text-sm rounded-xl bg-secondary/60 p-3">
          <strong>{uf(t.cuota_total_uf)}</strong> = <span className="text-emerald-700 font-semibold">{uf(t.ingreso_uf)} pagan</span> + <span className="text-orange-700 font-semibold">{uf(t.beca_uf)} beca EIM</span>.
          {" "}Solo incluye solicitudes resueltas por el comité y aceptadas por el socio ({num(t.socio_acepta)}). No entran las rechazadas por el comité, las que el socio no aceptó (no se hacen socios y no pagan), ni las que están en revisión o sin respuesta.
        </p>
      </div>
    </div>
  );
};

export default function AdminStats() {
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/admin/stats", { params: year ? { year } : {} })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, [year]);

  if (!data) {
    return <AdminLayout><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;
  }
  const t = data.total;
  const decided = t.aprobadas + t.rechazadas;
  const f = data.familias;
  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-accent" />
          <h1 className="text-2xl font-bold">Estadísticas de becas</h1>
        </div>
        <label className="flex items-center gap-2 text-sm">Año
          <select value={data.year || ""} onChange={(e) => setYear(Number(e.target.value))} className="h-10 rounded-xl border border-border px-3 bg-white" data-testid="stats-year-select">
            {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Montos en UF anuales según la cuota de cada categoría. Solo cuentan las becas resueltas por el comité y aceptadas por el socio: "Beca otorgada" es el descuento que da el EIM y "Ingreso generado" es lo que pagan esos socios después del descuento. Quienes rechazan el resultado no se hacen socios y no pagan.</p>

      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Solicitudes enviadas" value={num(t.solicitadas)} hint={`${plural(data.borradores, "borrador sin enviar", "borradores sin enviar")} · ${plural(data.registrados, "socio registrado", "socios registrados")} en total (todos los años)`} testId="kpi-solicitadas" />
          <Kpi label="Becas aprobadas" value={num(t.aprobadas)} hint={`${plural(t.rechazadas, "rechazada", "rechazadas")} · ${num(t.en_proceso + t.info)} por resolver`} tone="text-emerald-700" testId="kpi-aprobadas" />
          <Kpi label="% de beca promedio" value={pct(t.pct_promedio)} hint={`Pedían en promedio ${pct(t.pct_solicitado_promedio)} · el sistema sugería ${pct(t.pct_sugerido_promedio)}`} testId="kpi-pct" />
          <Kpi label="Socios que aceptan" value={pct(t.tasa_aceptacion)} hint={`${num(t.socio_acepta)} aceptan · ${num(t.socio_no_acepta)} no aceptan · ${num(t.socio_sin_respuesta)} sin responder`} testId="kpi-aceptacion" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi label="Socios que rechazan el resultado" value={pct(t.tasa_rechazo_socio)} hint={`${plural(t.socio_no_acepta, "socio no aceptó", "socios no aceptaron")} la beca ofrecida y quedan fuera del estadio`} tone="text-red-700" testId="kpi-rechazo-socio" />
          <Kpi label="Desviación promedio" value={dev(t.desviacion_promedio)} hint={`Diferencia entre el % que pidieron y el % otorgado (${plural(t.desviacion_casos, "caso que pidió un %", "casos que pidieron un %")}). Positivo = pidieron más de lo otorgado`} testId="kpi-desviacion" />
          <Kpi label="Rechazadas por el comité" value={pct(t.tasa_rechazo_comite)} hint={`${plural(t.rechazadas, "solicitud rechazada", "solicitudes rechazadas")} de las decididas`} testId="kpi-rechazo-comite" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi label="Cuota completa (aceptaron)" value={uf(t.cuota_total_uf)} hint="Lo que pagarían sin beca los socios que aceptaron" testId="kpi-cuota" />
          <Kpi label="Beca otorgada" value={uf(t.beca_uf)} hint="Descuento del EIM a los socios que aceptaron" tone="text-orange-700" testId="kpi-beca-uf" />
          <Kpi label="Ingreso generado" value={uf(t.ingreso_uf)} hint="Lo que pagan los socios becados que aceptaron" tone="text-emerald-700" testId="kpi-ingreso-uf" />
        </div>

        <Section title="Los dos lados de la cuota" explain="De la cuota completa de los socios becados que aceptaron su resultado: cuánto pagan ellos y cuánto pone el EIM como beca." testId="stats-cuota-pie">
          <CuotaPie t={t} />
          <div className="mt-6">
            <p className="text-sm font-semibold mb-2">Por categoría (UF)</p>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.por_categoria} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
                  <XAxis dataKey="categoria" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => uf(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-slate-700">{v}</span>}
                    payload={SLICES.map((s) => ({ value: s.name, type: "square", color: s.color, id: s.key }))} />
                  {SLICES.map((s) => <Bar key={s.key} dataKey={s.key} name={s.name} stackId="cuota" fill={s.color} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Section>

        <Section title="Por categoría" explain={`Solicitudes, resultado y montos de cada categoría. Tasa de aprobación del año: ${decided ? Math.round((100 * t.aprobadas) / decided) : 0}% de las decididas.`} testId="stats-by-category">
          <Chart data={data.por_categoria} x="categoria" bars={[
            { key: "solicitadas", name: "Solicitadas", color: "#64748B" },
            { key: "aprobadas", name: "Aprobadas", color: "#047857" },
            { key: "rechazadas", name: "Rechazadas", color: "#B91C1C" }]} />
          <div className="mt-6"><CategoryTable rows={data.por_categoria} total={t} /></div>
          <div className="mt-6">
            <p className="text-sm font-semibold mb-2">Beca otorgada vs. ingreso generado (UF)</p>
            <Chart data={data.por_categoria} x="categoria" bars={[
              { key: "beca_uf", name: "Beca otorgada", color: "#C2410C" },
              { key: "ingreso_uf", name: "Ingreso generado", color: "#047857" }]} />
          </div>
        </Section>

        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="Solicitantes por edad" explain="Edad del titular de la solicitud." testId="stats-by-age">
            <Chart data={data.por_edad} x="grupo" height={220} bars={[
              { key: "solicitudes", name: "Solicitudes", color: "#0F2942" },
              { key: "aprobadas", name: "Aprobadas", color: "#047857" }]} />
            <table className="w-full text-sm mt-4">
              <thead className="text-[11px] uppercase text-slate-500 text-left"><tr><th className="py-1">Grupo</th><th>Solicitudes</th><th>Aprobadas</th><th>% promedio</th></tr></thead>
              <tbody>{data.por_edad.map((g) => <tr key={g.grupo} className="border-t border-border"><td className="py-1.5">{g.grupo}</td><td>{g.solicitudes}</td><td>{g.aprobadas}</td><td>{pct(g.pct_promedio)}</td></tr>)}</tbody>
            </table>
          </Section>
          <Section title="Familias con hijos y colegio" explain="Solo solicitudes con hijos declarados. IH = algún hijo en el Instituto Hebreo (o familia IAJAD)." testId="stats-families">
            <div className="grid grid-cols-2 gap-3">
              <FamilyBox label="Familias con hijos" value={f.con_hijos} testId="fam-con-hijos" />
              <FamilyBox label="Son del Instituto Hebreo" value={f.ih} of={f.con_hijos} testId="fam-ih" />
              <FamilyBox label={`Tienen beca IH${f.beca_ih_promedio ? ` (prom. ${f.beca_ih_promedio}%)` : ""}`} value={f.con_beca_ih} of={f.con_hijos} testId="fam-beca-ih" />
              <FamilyBox label="De otros colegios" value={f.otros_colegios} of={f.con_hijos} testId="fam-otros" />
            </div>
            {f.sin_hijos_en_colegio > 0 && <p className="text-xs text-muted-foreground mt-3">{plural(f.sin_hijos_en_colegio, "familia", "familias")} con hijos sin colegio declarado.</p>}
          </Section>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="Tamaño del grupo familiar" explain="Personas que viven en el hogar según la solicitud." testId="stats-household">
            <Chart data={data.tamano_familia} x="personas" height={200} bars={[{ key: "familias", name: "Familias", color: "#0F2942" }]} />
          </Section>
          <Section title="Otros datos" testId="stats-other">
            <ul className="text-sm space-y-2">
              <li><strong>{num(data.modo_solicitud.pide_porcentaje)}</strong> pidieron un % específico y <strong>{num(data.modo_solicitud.eim_decide)}</strong> dejaron que el EIM decida.</li>
              <li><strong>{num(data.reevaluaciones)}</strong> solicitudes pidieron reevaluación.</li>
              <li>Ingreso bruto familiar promedio de los solicitantes: <strong>{clp(t.ingreso_familiar_promedio)}</strong> mensuales.</li>
              {data.por_categoria.filter((c) => c.ingreso_familiar_promedio).map((c) => (
                <li key={c.categoria} className="text-muted-foreground">· {c.categoria}: {clp(c.ingreso_familiar_promedio)}</li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </AdminLayout>
  );
}
